export type QuickSearchScope =
  | "all"
  | "page"
  | "sku"
  | "wiki"
  | "po"
  | "gipi"
  | "gosi"
  | "rfq"
  | "rma"
  | "case"
  | "event"
  | "ncr"
  | "rca"
  | "car"
  | "loan"
  | "batch"
  | "so"
  | "end-user"
  | "client"
  | "shipment"
  | "supplier"
  | "field";

/** Longer tokens first so `purchase-invoices` wins over `purchase`. */
const SCOPE_TOKEN_TO_SCOPE: Record<string, QuickSearchScope> = {
  "purchase-invoices": "gipi",
  "purchase-invoice": "gipi",
  "purchase-orders": "po",
  "purchase-order": "po",
  "sales-invoices": "gosi",
  "sales-invoice": "gosi",
  "sales-orders": "so",
  "sales-order": "so",
  "end-users": "end-user",
  "end-user": "end-user",
  endusers: "end-user",
  enduser: "end-user",
  customers: "client",
  customer: "client",
  clients: "client",
  client: "client",
  shipments: "shipment",
  shipment: "shipment",
  suppliers: "supplier",
  supplier: "supplier",
  vendors: "supplier",
  vendor: "supplier",
  ven: "supplier",
  batches: "batch",
  batch: "batch",
  fields: "field",
  field: "field",
  pages: "page",
  page: "page",
  skus: "sku",
  sku: "sku",
  wiki: "wiki",
  gipi: "gipi",
  tsh: "gipi",
  gosi: "gosi",
  gpo: "po",
  pos: "po",
  po: "po",
  purchase: "po",
  gso: "so",
  so: "so",
  sales: "so",
  rfqs: "rfq",
  rfq: "rfq",
  rmas: "rma",
  rma: "rma",
  "corrective-actions": "car",
  "analysis-loans": "loan",
  cases: "case",
  case: "case",
  events: "event",
  event: "event",
  ncrs: "ncr",
  ncr: "ncr",
  rcas: "rca",
  rca: "rca",
  loans: "loan",
  loan: "loan",
  cars: "car",
  car: "car",
  ship: "shipment",
};

const SCOPE_TOKEN_PATTERN = Object.keys(SCOPE_TOKEN_TO_SCOPE).join("|");

const AT_SCOPE_RE = new RegExp(`^@(${SCOPE_TOKEN_PATTERN})(?:\\s+|$)`, "i");
const COLON_SCOPE_RE = new RegExp(`^(${SCOPE_TOKEN_PATTERN}):\\s*(.*)$`, "i");

/** Distinct document-type codes glued to a number: GIPI3569, GPO1909, GSO-201625. */
const GLUED_DOCTYPE_RE = /^(gosi|gipi|gpo|gso|tsh|rfq|rma)[\s\-:]*(\d[\w\-]*)$/i;

const GLUED_PREFIX_TO_SCOPE: Record<string, QuickSearchScope> = {
  gipi: "gipi",
  tsh: "gipi",
  gpo: "po",
  gso: "so",
  gosi: "gosi",
  rfq: "rfq",
  rma: "rma",
};

function scopeForToken(token: string): QuickSearchScope {
  return SCOPE_TOKEN_TO_SCOPE[token] ?? "page";
}

export function parseQuickSwitcherScopedQuery(raw: string): { scope: QuickSearchScope; term: string } {
  const trimmed = raw.trimStart();
  const atMatch = AT_SCOPE_RE.exec(trimmed);
  if (atMatch) {
    const token = atMatch[1]?.toLowerCase() ?? "";
    return { scope: scopeForToken(token), term: trimmed.slice(atMatch[0].length) };
  }

  const colonMatch = COLON_SCOPE_RE.exec(trimmed);
  if (colonMatch) {
    const token = colonMatch[1]?.toLowerCase() ?? "";
    return { scope: scopeForToken(token), term: colonMatch[2] ?? "" };
  }

  const gluedMatch = GLUED_DOCTYPE_RE.exec(trimmed.trim());
  if (gluedMatch) {
    const prefix = gluedMatch[1]?.toLowerCase() ?? "";
    const remainder = gluedMatch[2] ?? "";
    const scope = GLUED_PREFIX_TO_SCOPE[prefix];
    if (scope != null && remainder !== "") {
      return { scope, term: remainder };
    }
  }

  return { scope: "all", term: raw };
}
